import { useEffect, useState } from 'react';
import { FileText, RotateCcw, Search } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

import { ReportViewer } from './ReportViewer';
import { ShareReportDialog } from './ShareReportDialog';
import { ReportPositionFilter } from '@/components/filters/ReportPositionFilter';
import { ReportPeriodFilter } from '@/components/filters/ReportPeriodFilter';
import { ModernPagination } from '@/components/ui/modern-pagination';
import { useReports } from '@/hooks/useReports';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const SortIcon = () => (
  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.33325 14.1061V7.43945M5.33325 14.1061L3.33325 12.1061M5.33325 14.1061L7.33325 12.1061M10.6666 3.43945V10.1061M10.6666 3.43945L12.6666 5.43945M10.6666 3.43945L8.66658 5.43945"
      stroke="#8F949D"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ViewIcon = () => (
  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.3866 8.31806C10.3866 9.63806 9.31995 10.7047 7.99995 10.7047C6.67995 10.7047 5.61328 9.63806 5.61328 8.31806C5.61328 6.99806 6.67995 5.9314 7.99995 5.9314C9.31995 5.9314 10.3866 6.99806 10.3866 8.31806Z"
      stroke="#40E0D0"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.9999 13.8314C10.3532 13.8314 12.5466 12.4448 14.0732 10.0448C14.6732 9.10476 14.6732 7.52476 14.0732 6.58476C12.5466 4.18476 10.3532 2.7981 7.9999 2.7981C5.64656 2.7981 3.45323 4.18476 1.92656 6.58476C1.32656 7.52476 1.32656 9.10476 1.92656 10.0448C3.45323 12.4448 5.64656 13.8314 7.9999 13.8314Z"
      stroke="#40E0D0"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmailIcon = () => (
  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.3335 13.9847H4.66683C2.66683 13.9847 1.3335 12.9847 1.3335 10.6514V5.9847C1.3335 3.65137 2.66683 2.65137 4.66683 2.65137H11.3335C13.3335 2.65137 14.6668 3.65137 14.6668 5.9847V10.6514C14.6668 12.9847 13.3335 13.9847 11.3335 13.9847Z"
      stroke="#FF7F50"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.3332 6.31812L9.2465 7.98478C8.55983 8.53145 7.43317 8.53145 6.7465 7.98478L4.6665 6.31812"
      stroke="#FF7F50"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PdfIcon = () => (
  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.6667 7.65137C12.6667 7.10603 12.6667 6.6047 12.5653 6.36003C12.464 6.11537 12.2713 5.92203 11.8853 5.5367L8.728 2.37937C8.39533 2.0467 8.22933 1.8807 8.02267 1.78203C7.97989 1.76149 7.93605 1.74324 7.89133 1.72737C7.676 1.65137 7.44067 1.65137 6.97067 1.65137C4.80733 1.65137 3.72533 1.65137 2.99267 2.24203C2.84472 2.36133 2.70996 2.49609 2.59067 2.64403C2 3.37803 2 4.4587 2 6.62203V9.65137C2 12.1654 2 13.4227 2.78133 14.2034C3.56267 14.984 4.81933 14.9847 7.33333 14.9847H12.6667M8 1.9847V2.31803C8 4.20337 8 5.1467 8.586 5.73203C9.17133 6.31803 10.1147 6.31803 12 6.31803H12.3333"
      stroke="#32CD32"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.9998 9.65137H12.6665C12.4897 9.65137 12.3201 9.72161 12.1951 9.84663C12.0701 9.97165 11.9998 10.1412 11.9998 10.318V11.318M11.9998 11.318V12.9847M11.9998 11.318H13.6665M4.6665 12.9847V11.6514M4.6665 11.6514V9.65137H5.6665C5.93172 9.65137 6.18607 9.75672 6.37361 9.94426C6.56115 10.1318 6.6665 10.3862 6.6665 10.6514C6.6665 10.9166 6.56115 11.1709 6.37361 11.3585C6.18607 11.546 5.93172 11.6514 5.6665 11.6514H4.6665ZM8.33317 9.65137H9.1905C9.82184 9.65137 10.3332 10.1487 10.3332 10.7627V11.8734C10.3332 12.4867 9.82117 12.9847 9.1905 12.9847H8.33317V9.65137Z"
      stroke="#32CD32"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ScoreBadge = ({ score }: { score: number }) => {
  const getScoreClasses = (value: number) => {
    if (value >= 8.0) {
      return {
        containerClass: 'outline-green-500',
        textClass: 'text-green-500',
      };
    }
    if (value >= 6.0) {
      return {
        containerClass: 'outline-yellow-400',
        textClass: 'text-yellow-400',
      };
    }
    return {
      containerClass: 'outline-rose-500',
      textClass: 'text-rose-500',
    };
  };

  const classes = getScoreClasses(score);

  return (
    <div
      className={`inline-flex items-center justify-center gap-2.5 rounded-md px-2 py-[5px] outline outline-1 outline-offset-[-1px] ${classes.containerClass}`}
    >
      <div
        className={`justify-start ${classes.textClass} text-xs font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight`}
      >
        {score.toFixed(1)}
      </div>
    </div>
  );
};

const ActionButtons = ({
  onView,
  onEmail,
  onPdf,
}: {
  onView: () => void;
  onEmail: () => void;
  onPdf: () => void;
}) => (
  <div className="flex items-center gap-3">
    <button
      onClick={onView}
      className="flex items-center gap-1 text-[13px] text-[hsl(var(--app-text-muted))] transition-opacity hover:opacity-70"
    >
      <ViewIcon />
      <div className="text-xs font-medium">View</div>
    </button>
    <div className="h-4 w-px bg-[hsl(var(--app-border-subtle))]" />
    <button
      onClick={onEmail}
      className="flex items-center gap-1 text-[13px] text-[hsl(var(--app-text-muted))] transition-opacity hover:opacity-70"
    >
      <EmailIcon />
      <div className="text-xs font-medium">Email</div>
    </button>
    <div className="h-4 w-px bg-[hsl(var(--app-border-subtle))]" />
    <button
      onClick={onPdf}
      className="flex items-center gap-1 text-[13px] text-[hsl(var(--app-text-muted))] transition-opacity hover:opacity-70"
    >
      <PdfIcon />
      <div className="text-xs font-medium">Pdf</div>
    </button>
  </div>
);

interface ReportsSectionProps {
  initialSelectedReportId?: string;
  fromDashboard?: boolean;
  fromNotification?: boolean;
  urlFilter?: string | null;
  onResetFilter?: () => void;
}

export const ReportsSection = ({
  initialSelectedReportId,
  fromDashboard,
  fromNotification,
  urlFilter,
  onResetFilter,
}: ReportsSectionProps) => {
  const { reports } = useReports();
  const { toast } = useToast();

  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPosition, setFilterPosition] = useState<string>('tous');
  const [filterPeriod, setFilterPeriod] = useState<string>('tous');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [specialFilter, setSpecialFilter] = useState<string | null>(null);
  const [unsharedCampaignIds, setUnsharedCampaignIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (urlFilter) {
      setSpecialFilter(urlFilter);
    } else {
      setSpecialFilter(null);
    }
  }, [urlFilter]);

  useEffect(() => {
    if (initialSelectedReportId && (fromDashboard || fromNotification)) {
      setSelectedReportId(initialSelectedReportId);
    }
  }, [initialSelectedReportId, fromDashboard, fromNotification]);

  useEffect(() => {
    const loadUnsharedReports = async () => {
      if (specialFilter === 'not-shared') {
        const { data: allReports } = await supabase
          .from('campaign_reports')
          .select('campaign_id');

        if (!allReports) return;

        const { data: sharedReports } = await supabase
          .from('message_logs')
          .select('campaign_id')
          .like('email_type', 'report_shared%')
          .eq('status', 'sent');

        const sharedIds = new Set((sharedReports || []).map((item: any) => item.campaign_id));
        const unsharedIds = (allReports as any[])
          .filter((item: any) => !sharedIds.has(item.campaign_id))
          .map((item: any) => item.campaign_id);

        setUnsharedCampaignIds(new Set(unsharedIds));
      }
    };

    loadUnsharedReports();
  }, [specialFilter]);

  const uniquePositions = Array.from(
    new Set(
      (reports
        ?.map((report) => report.campaigns?.target_position)
        .filter(Boolean)
        .filter((position) => typeof position === 'string' && position.trim() !== '') as string[]) || []
    )
  );

  const filteredReports = reports?.filter((report) => {
    if (specialFilter === 'not-shared' && !unsharedCampaignIds.has(report.campaign_id)) {
      return false;
    }

    const matchesSearch =
      report.campaigns?.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.campaigns?.target_position?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPosition =
      filterPosition === 'tous' || report.campaigns?.target_position === filterPosition;

    let matchesPeriod = true;
    if (filterPeriod !== 'tous') {
      const reportDate = new Date(report.generated_at);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - reportDate.getTime()) / (1000 * 60 * 60 * 24));

      switch (filterPeriod) {
        case '7-jours':
          matchesPeriod = daysDiff <= 7;
          break;
        case '30-jours':
          matchesPeriod = daysDiff <= 30;
          break;
        case '90-jours':
          matchesPeriod = daysDiff <= 90;
          break;
      }
    }

    return matchesSearch && matchesPosition && matchesPeriod;
  });

  const totalItems = filteredReports?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReports = filteredReports?.slice(startIndex, endIndex);

  const handleViewReport = (reportId: string) => {
    setSelectedReportId(reportId);
  };

  const handleShareReport = (report: any) => {
    setSelectedReport(report);
    setShareDialogOpen(true);
  };

  const handleDownloadPDF = (report: any) => {
    if (!report.campaigns?.candidate_name) {
      toast({
        title: 'Erreur',
        description: 'Impossible de télécharger le rapport : données manquantes',
        variant: 'destructive',
      });
      return;
    }

    setSelectedReportId(report.id);

    toast({
      title: 'Ouverture du rapport',
      description:
        "Le rapport s'ouvre. Utilisez le bouton PDF dans le rapport pour télécharger.",
    });
  };

  const handleBackToList = () => {
    setSelectedReportId(null);
  };

  if (selectedReportId) {
    return <ReportViewer reportId={selectedReportId} onBack={handleBackToList} />;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <section className="app-surface rounded-[22px] p-4 md:p-5">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-[hsl(var(--app-text-strong))] md:text-[20px]">
                Rapports de campagne
              </h2>
            </div>
          </div>

          <div className="rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] p-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex h-10 w-full items-center gap-2 rounded-[14px] border border-[hsl(var(--app-border-subtle))] bg-white px-3 shadow-[0_6px_18px_rgba(15,23,42,0.04)] lg:max-w-[420px]">
                <Search className="h-4 w-4 text-[hsl(var(--app-text-muted))]" />
                <input
                  type="text"
                  placeholder="Rechercher par nom de candidat."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 bg-transparent text-[14px] text-[hsl(var(--app-text-strong))] outline-none placeholder:text-[hsl(var(--app-text-muted))]"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <ReportPositionFilter
                  onPositionChange={setFilterPosition}
                  uniquePositions={uniquePositions}
                  selectedPosition={filterPosition}
                />

                {urlFilter && (
                  <button
                    onClick={onResetFilter}
                    className="inline-flex h-10 items-center gap-2 rounded-[14px] border border-emerald-200/80 bg-emerald-50 px-3.5 text-[13px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Réinitialiser
                  </button>
                )}

                <ReportPeriodFilter
                  onPeriodChange={setFilterPeriod}
                  selectedPeriod={filterPeriod}
                />
              </div>
            </div>
          </div>

          {!paginatedReports || paginatedReports.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="mx-auto mb-4 h-12 w-12 text-[hsl(var(--app-text-subtle))]" />
              <h3 className="mb-2 text-lg font-semibold text-[hsl(var(--app-text-strong))]">
                Aucun rapport trouvé
              </h3>
              <p className="text-[14px] text-[hsl(var(--app-text-muted))]">
                Essayez de modifier vos critères de recherche.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-[20px] border border-[hsl(var(--app-border-subtle))] bg-white">
              <div className="flex border-b border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))]/70">
                <div className="flex w-52 items-center gap-1.5 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
                    Candidat
                  </div>
                  <SortIcon />
                </div>
                <div className="flex w-80 items-center gap-1.5 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
                    Position
                  </div>
                  <SortIcon />
                </div>
                <div className="flex flex-1 items-center gap-1.5 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
                    Score
                  </div>
                  <SortIcon />
                </div>
                <div className="flex flex-1 items-center gap-1.5 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
                    Date
                  </div>
                  <SortIcon />
                </div>
                <div className="flex w-64 items-center px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">
                    Actions
                  </div>
                </div>
              </div>

              {paginatedReports.map((report) => (
                <div
                  key={report.id}
                  className="flex border-b border-[hsl(var(--app-border-subtle))] transition-colors hover:bg-[hsl(var(--app-surface-muted))]/40"
                >
                  <div className="flex w-52 items-center px-4 py-3.5">
                    <div className="text-sm font-medium text-[hsl(var(--app-text-strong))]">
                      {report.campaigns?.candidate_name || 'Candidat inconnu'}
                    </div>
                  </div>

                  <div className="flex w-80 items-center px-4 py-3.5">
                    <div className="text-sm text-[hsl(var(--app-text-muted))]">
                      {report.campaigns?.target_position || 'Poste non spécifié'}
                    </div>
                  </div>

                  <div className="flex flex-1 items-center px-4 py-3.5">
                    <ScoreBadge score={report.overall_avg} />
                  </div>

                  <div className="flex flex-1 items-center px-4 py-3.5">
                    <div className="text-sm text-[hsl(var(--app-text-muted))]">
                      {format(new Date(report.generated_at), 'dd/MM/yyyy', { locale: fr })}
                    </div>
                  </div>

                  <div className="flex w-64 items-center px-4 py-3.5">
                    <ActionButtons
                      onView={() => handleViewReport(report.id)}
                      onEmail={() => handleShareReport(report)}
                      onPdf={() => handleDownloadPDF(report)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <ModernPagination
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}

          {selectedReport && (
            <ShareReportDialog
              open={shareDialogOpen}
              onOpenChange={setShareDialogOpen}
              report={selectedReport}
            />
          )}
        </div>
      </section>
    </div>
  );
};
